import React from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import { PrimeBroker, EntityIdAndType, EntityTypeEnum, WorkflowActions, EntityStatus } from 'api'
import { MultiTextInput, KeyValuePair } from 'view/components/inputs'
import { CorrelatedResponseCallback, EntityTypeIdRecord } from 'store/notification/notificationTypes'
import { createWorkflowPrimeBrokersSelector } from 'selectors/workflowSelectors'
import { canManagePrimeBrokersSelector } from 'selectors/permissionSelectors'
import { Locale, Translation } from 'locale'

export type PrimeBrokersPanelProps = OwnProps & StateProps & DispatchProps

interface OwnProps {
  workflowId: string
  organisationId: string
  readOnly: boolean
}

interface StateProps {
  primeBrokers: PrimeBroker[]
  canManagePrimeBrokers: boolean
}

interface DispatchProps {
  onUpdate: (payload: { updatedEntity: PrimeBroker; workflowId: string } & EntityIdAndType) => unknown
  onAddNew: (workflowId: string, entityTypeEnum: EntityTypeEnum, correlatedResponseCallback?: CorrelatedResponseCallback) => unknown
  onDelete: (entitiesToDelete: EntityIdAndType[], workflowId: string) => unknown
}

const workflowIdSelector = (_state: {}, ownProps: PrimeBrokersPanelProps) => ownProps.workflowId

const mapStateToProps = createStructuredSelector({
  primeBrokers: createWorkflowPrimeBrokersSelector(workflowIdSelector),
  canManagePrimeBrokers: canManagePrimeBrokersSelector
})

const mapDispatchToProps = {
  onUpdate: WorkflowActions.createEntityUpdateReferenceData,
  onAddNew: WorkflowActions.createAddToExistingWorkflow,
  onDelete: WorkflowActions.createEntityDeleteReferenceData
}

export const PrimeBrokersPanelComponent: React.FunctionComponent<PrimeBrokersPanelProps> = props => {
  const { workflowId, organisationId, primeBrokers, readOnly, canManagePrimeBrokers, onUpdate, onAddNew, onDelete } = props

  const primeBrokerKeyValuePairs: KeyValuePair<string>[] = primeBrokers.map(primeBroker => ({
    key: primeBroker.id,
    value: primeBroker.name
  }))

  const handleUpdate = (newPrimeBrokerKeyValuePairs: KeyValuePair<string>[]) => {
    // Find prime brokers that have been deleted
    const newPrimeBrokerIds = newPrimeBrokerKeyValuePairs.map(newPrimeBrokerKeyValuePair => newPrimeBrokerKeyValuePair.key)
    const primeBrokerEntitiesToDelete = primeBrokerKeyValuePairs
      .filter(primeBrokerKeyValuePair => !newPrimeBrokerIds.includes(primeBrokerKeyValuePair.key))
      .map(primeBrokerKeyValuePair => ({
        entityId: primeBrokerKeyValuePair.key,
        entityType: EntityTypeEnum.PRIME_BROKER
      }))
    if (primeBrokerEntitiesToDelete.length > 0) {
      onDelete(primeBrokerEntitiesToDelete, workflowId)
    }

    newPrimeBrokerKeyValuePairs.forEach(newPrimeBrokerKeyValuePair => {
      const existingPrimeBrokerKeyValuePair = primeBrokerKeyValuePairs.find(
        primeBrokerKeyValuePair => primeBrokerKeyValuePair.key === newPrimeBrokerKeyValuePair.key
      )
      if (!existingPrimeBrokerKeyValuePair) {
        // Add new prime brokers
        onAddNew(workflowId, EntityTypeEnum.PRIME_BROKER, (_workflowId, newlyAddedEntityIdAndTypes: EntityTypeIdRecord) => {
          onUpdate({
            entityId: newlyAddedEntityIdAndTypes[EntityTypeEnum.PRIME_BROKER],
            entityType: EntityTypeEnum.PRIME_BROKER,
            updatedEntity: {
              id: newlyAddedEntityIdAndTypes[EntityTypeEnum.PRIME_BROKER],
              name: newPrimeBrokerKeyValuePair.value,
              organisationId,
              status: EntityStatus.ENABLED
            },
            workflowId
          })
        })
      } else if (existingPrimeBrokerKeyValuePair.value !== newPrimeBrokerKeyValuePair.value) {
        // Update existing prime brokers if name changes
        onUpdate({
          entityId: existingPrimeBrokerKeyValuePair.key,
          entityType: EntityTypeEnum.PRIME_BROKER,
          updatedEntity: {
            id: existingPrimeBrokerKeyValuePair.key,
            name: newPrimeBrokerKeyValuePair.value,
            organisationId,
            status: EntityStatus.ENABLED
          },
          workflowId
        })
      }
    })
  }

  return (
    <MultiTextInput
      readOnly={readOnly || !canManagePrimeBrokers}
      canClear={!readOnly && canManagePrimeBrokers}
      placeholder={Locale.translate(Translation.SC_ADD_PRIME_BROKER)}
      value={primeBrokerKeyValuePairs}
      getOption={(itemKey: string) => primeBrokers.find(primeBroker => primeBroker.id === itemKey) || null}
      getOptionText={(item: PrimeBroker) => item.name}
      clearButtonLabel={Locale.translate(Translation.SC_REMOVE_PRIME_BROKERS)}
      onUpdate={handleUpdate}
    />
  )
}

export const PrimeBrokersPanel = connect<StateProps, DispatchProps, OwnProps>(
  mapStateToProps,
  mapDispatchToProps
)(PrimeBrokersPanelComponent)
